import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { UMInputNumber } from '@urbanmetrix/um-kit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import L from 'leaflet';
import {
  createBuildableSegmentOffsets,
  deriveBuildableBorderFromLegalBorder,
  deriveBuildableOffsetPreviewSegments,
} from 'utils/buildableBorderHelper';
import { lv95ToWgs84ForOneCoordinate } from '../map/coordinateTransforms';
import { getLayerMap, getLayerOuterRing, useStableHandler, type Lv95Coordinate } from './cadastralShared';
import Style from '../styles/grid.tailwind';

const SEGMENT_IDLE_COLOR = '#f5f5f5';
const SEGMENT_DONE_COLOR = '#2e7d32';
const SEGMENT_ACTIVE_COLOR = '#0288d1';
const SEGMENT_WEIGHT = 6;
const INVALID_OFFSET_MESSAGE = 'Please enter an offset greater than or equal to 0 meters.';

type AreaOffsetEditorOptions = {
  area: Lv95Coordinate[];
  areaLabel: string;
  introTitle: string;
  inputName: string;
  layerRef: MutableRefObject<L.Polygon | null>;
  onApply: (area: Lv95Coordinate[]) => boolean | void;
  onError: (message: string) => void;
  onReset?: () => void;
  onStartSelecting?: () => void;
};

const useAreaOffsetEditor = ({
  area,
  areaLabel,
  introTitle,
  inputName,
  layerRef,
  onApply,
  onError,
  onReset = () => undefined,
  onStartSelecting = () => undefined,
}: AreaOffsetEditorOptions) => {
  const [introModalOpen, setIntroModalOpen] = useState(false);
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number | null>(null);
  const [segmentOffsets, setSegmentOffsets] = useState<Array<number | null>>([]);
  const [offsetInputValue, setOffsetInputValue] = useState(1);
  const [applyToAllSides, setApplyToAllSides] = useState(false);
  const [applyToRemainingSides, setApplyToRemainingSides] = useState(false);
  const [remainingOffsetInputValue, setRemainingOffsetInputValue] = useState(0);
  const segmentLayersRef = useRef<L.Layer[]>([]);
  const applyAreaRef = useRef(onApply);
  useEffect(() => {
    applyAreaRef.current = onApply;
  }, [onApply]);
  const applyArea = useCallback((nextArea: Lv95Coordinate[]) => applyAreaRef.current(nextArea), []);
  const showError = useStableHandler(onError);
  const notifyReset = useStableHandler(onReset);
  const notifyStartSelecting = useStableHandler(onStartSelecting);

  const clearSegmentLayers = useCallback(() => {
    segmentLayersRef.current.forEach((layer) => {
      layer.off();
      getLayerMap(layer)?.removeLayer(layer);
    });
    segmentLayersRef.current = [];
  }, []);

  const reset = useCallback(() => {
    clearSegmentLayers();
    setIsActive(false);
    setIntroModalOpen(false);
    setInputModalOpen(false);
    setSelectedSegmentIndex(null);
    setSegmentOffsets([]);
    setOffsetInputValue(1);
    setApplyToAllSides(false);
    setApplyToRemainingSides(false);
    setRemainingOffsetInputValue(0);
    notifyReset();
  }, [clearSegmentLayers, notifyReset]);

  const interrupt = useCallback(() => {
    if (isActive || introModalOpen || inputModalOpen) reset();
  }, [inputModalOpen, introModalOpen, isActive, reset]);

  const applyOffsets = useCallback(
    (offsets: number[]) => {
      const { buildableBorder, error } = deriveBuildableBorderFromLegalBorder(area, offsets);
      if (error) {
        showError(error);
        return false;
      }
      return applyArea(buildableBorder) !== false;
    },
    [applyArea, area, showError]
  );

  const open = useCallback(() => {
    if (area.length >= 3) setIntroModalOpen(true);
  }, [area.length]);

  const start = () => {
    if (applyToAllSides) {
      const offset = Number(offsetInputValue);
      if (!Number.isFinite(offset) || offset < 0) {
        showError(INVALID_OFFSET_MESSAGE);
        return;
      }
      if (applyOffsets(Array(area.length).fill(offset))) reset();
      return;
    }

    setSegmentOffsets(Array(area.length).fill(null));
    setOffsetInputValue(0);
    setSelectedSegmentIndex(null);
    setIntroModalOpen(false);
    setInputModalOpen(false);
    setIsActive(true);
    notifyStartSelecting();
  };

  const submitSelectedSide = () => {
    const offset = Number(applyToRemainingSides ? remainingOffsetInputValue : offsetInputValue);
    if (!Number.isFinite(offset) || offset < 0 || selectedSegmentIndex === null) {
      showError(INVALID_OFFSET_MESSAGE);
      return;
    }

    const offsets = createBuildableSegmentOffsets(
      area.length,
      selectedSegmentIndex,
      offset,
      applyToRemainingSides ? Number(remainingOffsetInputValue) : null
    );
    if (offsets.length > 0 && applyOffsets(offsets)) reset();
  };

  const closeInputModal = useCallback(() => {
    setInputModalOpen(false);
    setSelectedSegmentIndex(null);
    setOffsetInputValue(0);
    setApplyToAllSides(false);
    setApplyToRemainingSides(false);
    setRemainingOffsetInputValue(0);
  }, []);

  useEffect(() => {
    if (!isActive || area.length < 3) {
      clearSegmentLayers();
      return undefined;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const syncSegmentLayers = () => {
      if (cancelled) return;

      const map = getLayerMap(layerRef.current);
      const latLngs = getLayerOuterRing(layerRef.current);
      if (!map || latLngs.length < 3) {
        retryTimer = setTimeout(syncSegmentLayers, 50);
        return;
      }

      clearSegmentLayers();
      const previewSegments = deriveBuildableOffsetPreviewSegments(area, segmentOffsets);
      const segmentLayers = latLngs.flatMap((startLatLng, index) => {
        const endLatLng = latLngs[(index + 1) % latLngs.length];
        const isCompleted = segmentOffsets[index] != null && Number(segmentOffsets[index]) >= 0;
        const isSelected = index === selectedSegmentIndex;
        const color = isSelected ? SEGMENT_ACTIVE_COLOR : isCompleted ? SEGMENT_DONE_COLOR : SEGMENT_IDLE_COLOR;
        const openInputModal = () => {
          const currentOffset = segmentOffsets[index];
          setSelectedSegmentIndex(index);
          setOffsetInputValue(Number(currentOffset) >= 0 ? Number(currentOffset) : 0);
          setRemainingOffsetInputValue(Number(currentOffset) >= 0 ? Number(currentOffset) : 0);
          setApplyToRemainingSides(false);
          setApplyToAllSides(false);
          setInputModalOpen(true);
        };
        const layers: L.Layer[] = [];
        const previewSegment = previewSegments[index];

        if (isCompleted && previewSegment) {
          const previewStart = lv95ToWgs84ForOneCoordinate(previewSegment.start.x, previewSegment.start.y);
          const previewEnd = lv95ToWgs84ForOneCoordinate(previewSegment.end.x, previewSegment.end.y);
          layers.push(
            L.polyline([L.latLng(previewStart.lat, previewStart.lng), L.latLng(previewEnd.lat, previewEnd.lng)], {
              color,
              weight: SEGMENT_WEIGHT,
              opacity: 0.95,
              interactive: false,
            }).addTo(map)
          );
          const hitAreaLayer = L.polyline([startLatLng, endLatLng], {
            color,
            weight: Math.max(SEGMENT_WEIGHT + 8, 14),
            opacity: 0,
            interactive: true,
          }).addTo(map);
          hitAreaLayer.on('click', openInputModal);
          layers.push(hitAreaLayer);
          return layers;
        }

        const layer = L.polyline([startLatLng, endLatLng], {
          color,
          weight: SEGMENT_WEIGHT,
          opacity: 0.95,
          interactive: true,
        }).addTo(map);
        layer.on('click', openInputModal);
        layers.push(layer);
        return layers;
      });
      const vertexLayers = latLngs.map((latLng) =>
        L.circleMarker(latLng, {
          radius: 4,
          color: '#111',
          fillColor: '#111',
          fillOpacity: 1,
          weight: 1,
          interactive: false,
        }).addTo(map)
      );
      segmentLayersRef.current = [...segmentLayers, ...vertexLayers];
    };

    syncSegmentLayers();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      clearSegmentLayers();
    };
  }, [area, clearSegmentLayers, isActive, layerRef, segmentOffsets, selectedSegmentIndex]);

  useEffect(() => {
    if (!isActive) return;
    if (area.length < 3) {
      reset();
      return;
    }
    if (selectedSegmentIndex !== null && selectedSegmentIndex >= area.length) closeInputModal();
  }, [area.length, closeInputModal, isActive, reset, selectedSegmentIndex]);

  const dialogs = (
    <>
      <Modal open={introModalOpen} onClose={reset} className="custom-modal">
        <Box sx={{ ...Style.modal, width: 'auto', maxWidth: '560px', border: 'none', borderRadius: '4px' }}>
          <Typography variant="h6" component="h2">
            {introTitle}
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Select side of current {areaLabel} on map and enter inward offset in meters. Completed sides will be drawn
            directly at configured offset.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Single-side offset applies immediately. "Apply same offset" options still work for all remaining sides.
          </Typography>
          <FormControlLabel
            className="checkbox-row"
            sx={{ mt: 2 }}
            control={
              <Checkbox checked={applyToAllSides} onChange={(event) => setApplyToAllSides(event.target.checked)} />
            }
            label="Apply the same offset to all area sides"
          />
          {applyToAllSides && (
            <Box sx={{ mt: 1 }}>
              <UMInputNumber
                label="Offset [m]"
                name={inputName}
                value={offsetInputValue}
                width={84}
                maxErrorMessageWidth={276}
                onChange={(_name, value) => setOffsetInputValue(Number(value))}
                defaultValue={0}
                min={0}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: 3 }}>
            <Button variant="contained" className="modal-no-btn" onClick={reset}>
              CANCEL
            </Button>
            <Button variant="contained" className="modal-yes-btn" onClick={start}>
              {applyToAllSides ? 'DRAW' : 'START'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={inputModalOpen} onClose={closeInputModal} className="custom-modal">
        <Box sx={{ ...Style.modal, width: 'auto', maxWidth: '520px', border: 'none', borderRadius: '4px' }}>
          <Typography variant="h6" component="h2">
            {selectedSegmentIndex !== null ? `${areaLabel} side ${selectedSegmentIndex + 1}` : `${areaLabel} side`}
          </Typography>
          <Typography sx={{ mt: 2, mb: 2 }}>Enter the internal offset for the selected side.</Typography>
          <UMInputNumber
            label="Offset [m]"
            name={inputName}
            value={offsetInputValue}
            width={84}
            maxErrorMessageWidth={276}
            onChange={(_name, value) => setOffsetInputValue(Number(value))}
            defaultValue={0}
            min={0}
          />
          <FormControlLabel
            className="checkbox-row"
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={applyToRemainingSides}
                onChange={(event) => {
                  setApplyToRemainingSides(event.target.checked);
                  if (event.target.checked) setRemainingOffsetInputValue(Math.max(0, Number(offsetInputValue)));
                }}
              />
            }
            label="Apply the same offset to this side and all remaining unassigned sides"
          />
          {applyToRemainingSides && (
            <Box sx={{ mt: 1 }}>
              <UMInputNumber
                label="Shared offset [m]"
                name={`${inputName}Remaining`}
                value={remainingOffsetInputValue}
                width={84}
                maxErrorMessageWidth={276}
                onChange={(_name, value) => setRemainingOffsetInputValue(Number(value))}
                defaultValue={0}
                min={0}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: 3 }}>
            <Button variant="contained" className="modal-no-btn" onClick={closeInputModal}>
              CANCEL
            </Button>
            <Button variant="contained" className="modal-yes-btn" onClick={submitSelectedSide}>
              Ok
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );

  return {
    dialogs,
    interrupt,
    isActive,
    isEngaged: isActive || introModalOpen || inputModalOpen,
    open,
    reset,
  };
};

export default useAreaOffsetEditor;
